<template>
	<v-card :class="embedded ? '' : 'ma-5'" flat>
		<v-card-text :class="embedded ? 'api-explorer-embedded-card' : ''">
			<!-- Loading State -->
			<div v-if="loadingSpec" class="d-flex justify-center align-center py-8">
				<v-progress-circular indeterminate color="primary" />
			</div>

			<!-- Error State -->
			<v-alert
				v-else-if="specError"
				type="error"
				outlined
				dense
				class="mb-0"
			>
				{{ $t('failed-to-load-api') }}
			</v-alert>

			<!-- Main Content -->
			<div v-else class="api-explorer-content">
				<!-- Auth Token Section -->
				<div class="token-section mb-4">
					<div class="token-header clickable" @click="sections.token = !sections.token">
						<div class="d-flex align-center">
							<v-icon color="white" size="18" class="mr-2 collapse-icon" :class="{ collapsed: !sections.token }">mdi-chevron-down</v-icon>
							<v-icon color="white" size="20" class="mr-2">mdi-shield-key</v-icon>
							<span>{{ $t('authorization-token') }}</span>
							<v-chip
								x-small
								:color="hasToken ? 'green' : 'red'"
								dark
								class="ml-2"
							>
								{{ hasToken ? $t('api-status-active') : $t('missing') }}
							</v-chip>
						</div>
						<div class="d-flex align-center">
							<v-btn
								small
								text
								color="white"
								class="copy-token-btn"
								:disabled="!hasToken"
								@click.stop="copyToken"
							>
								<v-icon small left>mdi-content-copy</v-icon>
								{{ $t('copy-jwt') }}
							</v-btn>
						</div>
					</div>
					<v-expand-transition>
						<div v-show="sections.token" class="token-preview">
							<span class="token-value">{{ tokenPreview }}</span>
						</div>
					</v-expand-transition>
				</div>

				<!-- Endpoint Selector Section -->
				<div class="endpoint-section mb-4">
					<div class="endpoint-header clickable" @click="sections.endpoint = !sections.endpoint">
						<div class="d-flex align-center">
							<v-icon color="white" size="18" class="mr-2 collapse-icon" :class="{ collapsed: !sections.endpoint }">mdi-chevron-down</v-icon>
							<v-icon color="white" size="20" class="mr-2">mdi-api</v-icon>
							<span>{{ $t('api-endpoint') }}</span>
							<v-chip x-small class="ml-2" color="white" text-color="grey darken-2">
								{{ endpoints.length }} routes
							</v-chip>
						</div>
					</div>
					<v-expand-transition>
						<div v-show="sections.endpoint" class="endpoint-content">
							<!-- Search -->
							<v-text-field
								v-model="searchQuery"
								:placeholder="$t('search-endpoints')"
								prepend-inner-icon="mdi-magnify"
								clearable
								hide-details
								outlined
								dense
								class="mb-3"
							/>

							<!-- Manual Mode Toggle -->
							<div class="d-flex align-center mb-3">
								<v-switch
									v-model="manualMode"
									hide-details
									dense
									class="mt-0 pt-0"
									color="primary"
								/>
								<span class="text-caption ml-2">{{ $t('manual-mode') }}</span>
							</div>

							<!-- Manual Input -->
							<div v-if="manualMode" class="manual-input mb-3">
								<v-row dense>
									<v-col cols="3">
										<v-select
											v-model="selectedMethod"
											:items="httpMethods"
											:label="$t('method')"
											outlined
											dense
											hide-details
											:item-color="getMethodColor(selectedMethod)"
										>
											<template #selection="{ item }">
												<v-chip
													x-small
													:color="getMethodColor(item)"
													dark
													label
													class="method-chip"
												>
													{{ item }}
												</v-chip>
											</template>
											<template #item="{ item }">
												<v-chip
													x-small
													:color="getMethodColor(item)"
													dark
													label
													class="method-chip"
												>
													{{ item }}
												</v-chip>
											</template>
										</v-select>
									</v-col>
									<v-col cols="9">
										<v-text-field
											v-model="manualPath"
											:label="$t('path')"
											placeholder="/api/endpoint"
											outlined
											dense
											hide-details
										/>
									</v-col>
								</v-row>
							</div>

							<!-- Endpoint List -->
							<div v-else class="endpoint-list">
								<template v-if="groupedEndpoints.length">
									<div v-for="group in groupedEndpoints" :key="group.tag" class="endpoint-group">
										<div class="endpoint-group-header">
											<span class="group-tag-dot" :style="{ background: group.endpoints[0] ? getMethodColor(group.endpoints[0].method) : '#999' }" />
											<span class="group-tag-name">{{ group.tag }}</span>
											<span class="endpoint-count">{{ group.endpoints.length }}</span>
										</div>
										<div
											v-for="endpoint in group.endpoints"
											:key="endpoint.id"
											class="endpoint-item"
											:class="{ 'endpoint-selected': selectedEndpoint?.id === endpoint.id }"
											@click="selectEndpoint(endpoint)"
										>
											<div class="endpoint-item-main">
												<v-chip
													x-small
													:color="getMethodColor(endpoint.method)"
													dark
													label
													class="method-chip"
												>
													{{ endpoint.method }}
												</v-chip>
												<v-tooltip bottom>
													<template #activator="{ on, attrs }">
														<v-icon
															x-small
															:color="endpoint.isPublic ? 'green' : 'red'"
															class="security-icon"
															v-bind="attrs"
															v-on="on"
														>
															{{ endpoint.isPublic ? 'mdi-earth' : 'mdi-lock' }}
														</v-icon>
													</template>
													<span>{{ endpoint.isPublic ? $t('public') : $t('authorization-required') }}</span>
												</v-tooltip>
												<!-- eslint-disable-next-line vue/no-v-html -->
												<span class="endpoint-path" v-html="breakablePath(endpoint.path)" />
											</div>
											<div v-if="endpoint.summary" class="endpoint-summary">
												{{ endpoint.summary }}
											</div>
										</div>
									</div>
								</template>
								<div v-else class="no-results">
									<v-icon color="grey">mdi-magnify-close</v-icon>
									<span>{{ $t('no-endpoints-found') }}</span>
								</div>
							</div>
						</div>
					</v-expand-transition>
				</div>

				<!-- Selected Endpoint Details -->
				<div v-if="selectedEndpoint || manualMode" ref="requestSection" class="request-section mb-4">
					<div class="request-header clickable" @click="sections.request = !sections.request">
						<div class="d-flex align-center">
							<v-icon color="white" size="18" class="mr-2 collapse-icon" :class="{ collapsed: !sections.request }">mdi-chevron-down</v-icon>
							<v-icon color="white" size="20" class="mr-2">mdi-send</v-icon>
							<span>{{ $t('api-request') }}</span>
							<v-chip
								v-if="selectedEndpoint"
								x-small
								:color="getMethodColor(selectedEndpoint.method)"
								dark
								label
								class="ml-2"
							>
								{{ selectedEndpoint.method }}
							</v-chip>
						</div>
						<v-btn
							small
							:color="executing ? 'grey' : 'white'"
							:text="!executing"
							:loading="executing"
							class="execute-btn"
							@click.stop="executeRequest"
						>
							<v-icon small left>mdi-play</v-icon>
							{{ $t('execute') }}
						</v-btn>
					</div>
					<v-expand-transition>
						<div v-show="sections.request" class="request-content">
							<!-- Path with Parameters -->
							<div class="request-path mb-3">
								<span class="path-label">URL:</span>
								<code class="path-value">
									{{ fullRequestUrl }}
								</code>
							</div>

							<!-- Path Parameters -->
							<div v-if="pathParams.length" class="params-section mb-3">
								<div class="params-label">
									{{ $t('path-parameters') }}
								</div>
								<div v-for="param in pathParams" :key="param.name" class="param-row">
									<v-text-field
										v-model="paramValues[param.name]"
										:label="param.name"
										:placeholder="param.description || param.name"
										:hint="param.schema?.type"
										persistent-hint
										outlined
										dense
										class="param-input"
									/>
									<v-chip
										v-if="param.required"
										x-small
										color="red"
										dark
										class="ml-2"
									>
										{{ $t('api-required') }}
									</v-chip>
								</div>
							</div>

							<!-- Query Parameters -->
							<div v-if="queryParams.length" class="params-section mb-3">
								<div class="params-label">
									{{ $t('query-parameters') }}
								</div>
								<div v-for="param in queryParams" :key="param.name" class="param-row">
									<v-text-field
										v-model="paramValues[param.name]"
										:label="param.name"
										:placeholder="param.description || param.name"
										:hint="param.schema?.type"
										persistent-hint
										outlined
										dense
										class="param-input"
									/>
									<v-chip
										v-if="param.required"
										x-small
										color="red"
										dark
										class="ml-2"
									>
										{{ $t('api-required') }}
									</v-chip>
								</div>
							</div>

							<!-- Custom Query Parameters -->
							<div class="params-section mb-3">
								<div class="params-label d-flex align-center justify-space-between">
									<span>{{ $t('custom-query-parameters') }}</span>
									<v-btn x-small text color="primary" @click="addCustomQueryParam">
										<v-icon x-small left>mdi-plus</v-icon>
										{{ $t('add-parameter') }}
									</v-btn>
								</div>
								<div v-for="(param, index) in customQueryParams" :key="index" class="param-row custom-param-row">
									<v-text-field
										v-model="param.name"
										:placeholder="$t('parameter-name')"
										outlined
										dense
										hide-details
										class="param-name-input mr-2"
									/>
									<v-text-field
										v-model="param.value"
										:placeholder="$t('parameter-value')"
										outlined
										dense
										hide-details
										class="param-value-input"
									/>
									<v-btn
										icon
										x-small
										class="ml-2"
										color="red"
										@click="removeCustomQueryParam(index)"
									>
										<v-icon x-small>mdi-close</v-icon>
									</v-btn>
								</div>
								<div v-if="!customQueryParams.length" class="text-caption grey--text mt-2">
									{{ $t('no-custom-parameters') }}
								</div>
							</div>

							<!-- Request Body -->
							<div v-if="hasRequestBody" class="params-section mb-3">
								<div class="params-label">
									{{ $t('request-body') }}
								</div>

								<!-- Request Body Schema Hints -->
								<v-card
									v-if="requestBodyHints && requestBodyHints.length > 0"
									flat
									outlined
									dense
									class="mb-2 body-hints-card"
								>
									<v-card-text class="pa-3">
										<div class="body-hints-title text-caption font-weight-bold mb-2 d-flex align-center">
											<v-icon x-small class="mr-1">mdi-information-outline</v-icon>
											{{ $t('required-fields', { count: requestBodyHints.filter(h => h.required).length }) }}
										</div>
										<div class="body-hints-list">
											<div v-for="hint in requestBodyHints" :key="hint.name" class="body-hint-item text-caption mb-1">
												<div class="d-flex align-center">
													<span class="font-weight-bold" :style="{ color: hint.required ? '#d32f2f' : '#666' }">
														{{ hint.name }}
														<v-chip
															v-if="hint.required"
															x-small
															color="error"
															text-color="white"
															class="ml-1"
															label
														>
															{{ $t('required') }}
														</v-chip>
													</span>
													<span class="ml-2 grey--text text--darken-1">
														{{ hint.type }}
													</span>
												</div>
												<div v-if="hint.description" class="grey--text text-caption ml-4 mt-1">
													{{ hint.description }}
												</div>
											</div>
										</div>
									</v-card-text>
								</v-card>

								<v-textarea
									v-model="requestBody"
									placeholder="{&quot;key&quot;: &quot;value&quot;}"
									outlined
									dense
									rows="4"
									class="body-input"
									:class="{ 'json-invalid': bodyError }"
									:error-messages="bodyError"
									@input="validateJson"
									@paste="onPaste"
								/>
							</div>
						</div>
					</v-expand-transition>
				</div>

				<!-- Response Section -->
				<div v-if="hasResponse" ref="responseSection" class="response-section">
					<div class="response-header clickable" :class="responseHeaderClass" @click="sections.response = !sections.response">
						<div class="d-flex align-center">
							<v-icon color="white" size="18" class="mr-2 collapse-icon" :class="{ collapsed: !sections.response }">mdi-chevron-down</v-icon>
							<v-icon color="white" size="20" class="mr-2">mdi-code-json</v-icon>
							<span>{{ $t('response') }}</span>
							<v-chip
								v-if="responseStatus"
								x-small
								:color="responseStatusColor"
								dark
								class="ml-2"
							>
								{{ responseStatus }}
							</v-chip>
						</div>
						<div class="response-header-actions d-flex align-center">
							<span v-if="responseTime" class="response-time ml-2">{{ responseTime }}ms</span>
							<span v-if="responseSize !== null" class="response-size ml-2">{{ formatBytes(responseSize) }}</span>
							<v-btn
								v-if="responseHeaders"
								small
								text
								color="white"
								class="headers-toggle-btn mr-1"
								:class="{ 'headers-active': showHeaders }"
								@click.stop="showHeaders = !showHeaders"
							>
								<v-icon small left>mdi-swap-vertical</v-icon>
								{{ $t('headers') }}
								<span class="headers-count">({{ Object.keys(responseHeaders).length }})</span>
							</v-btn>
							<v-btn
								small
								text
								color="white"
								class="copy-response-btn"
								@click.stop="copyResponse"
							>
								<v-icon small left>mdi-content-copy</v-icon>
								{{ $t('copy') }}
							</v-btn>
						</div>
					</div>
					<v-expand-transition>
						<div v-show="sections.response" class="response-content">
							<!-- Response Headers Panel -->
							<v-expand-transition>
								<div v-show="showHeaders && responseHeaders" class="response-headers-panel">
									<div class="headers-panel-header">
										<span class="headers-panel-title">{{ $t('response-headers') }}</span>
										<v-tooltip bottom max-width="300">
											<template #activator="{ on, attrs }">
												<v-icon
													x-small
													color="grey lighten-1"
													class="ml-2"
													v-bind="attrs"
													v-on="on"
												>
													mdi-information-outline
												</v-icon>
											</template>
											<span>{{ $t('headers-cors-info') }}</span>
										</v-tooltip>
									</div>
									<div class="headers-grid">
										<template v-for="(value, key) in responseHeaders">
											<span :key="key + '-name'" class="header-name">{{ key }}</span>
											<span :key="key + '-value'" class="header-value">{{ value }}</span>
										</template>
									</div>
								</div>
							</v-expand-transition>
							<pre v-if="responseStatus && !responseError" class="response-body">{{ formattedResponse }}</pre>
							<div v-else-if="responseError" class="response-error">
								{{ responseError }}
							</div>
						</div>
					</v-expand-transition>
				</div>
			</div>
		</v-card-text>
	</v-card>
</template>

<script>
import { mapState } from "vuex";

export default {
	name: "ApiExplorer",

	props: {
		embedded: {
			type: Boolean,
			default: false
		}
	},

	data() {
		return {
			loadingSpec: false,
			specError: null,
			spec: null,
			endpoints: [],
			searchQuery: "",
			manualMode: false,
			selectedMethod: "GET",
			manualPath: "",
			selectedEndpoint: null,
			paramValues: {},
			customQueryParams: [],
			requestBody: "{\n\t\n}",
			bodyError: "",
			executing: false,
			response: null,
			responseError: null,
			responseStatus: null,
			responseTime: null,
			responseSize: null,
			responseHeaders: null,
			showHeaders: false,
			httpMethods: ["GET", "POST", "PUT", "PATCH", "DELETE"],
			sections: {
				token: false,
				endpoint: true,
				request: true,
				response: true
			}
		};
	},

	computed: {
		...mapState("login", ["jwt"]),

		hasToken() {
			return !!this.jwt;
		},

		tokenPreview() {
			if (!this.jwt) return this.$t("no-token-available");
			return this.jwt;
		},

		filteredEndpoints() {
			if (!this.searchQuery) return this.endpoints;
			const query = this.searchQuery.toLowerCase();

			return this.endpoints.filter(
				ep =>
					ep.path.toLowerCase().includes(query) ||
					ep.method.toLowerCase().includes(query) ||
					(ep.summary && ep.summary.toLowerCase().includes(query)) ||
					(ep.tags && ep.tags.some(t => t.toLowerCase().includes(query)))
			);
		},

		groupedEndpoints() {
			const groups = {};

			for (const endpoint of this.filteredEndpoints) {
				const tag = endpoint.tags?.[0] || "Other";

				if (!groups[tag]) {
					groups[tag] = [];
				}
				groups[tag].push(endpoint);
			}
			// Sort groups alphabetically
			return Object.entries(groups)
				.sort(([a], [b]) => a.localeCompare(b))
				.map(([tag, endpoints]) => ({ tag, endpoints }));
		},

		pathParams() {
			if (!this.selectedEndpoint?.parameters) return [];
			return this.selectedEndpoint.parameters.filter(p => p.in === "path");
		},

		queryParams() {
			if (!this.selectedEndpoint?.parameters) return [];
			return this.selectedEndpoint.parameters.filter(p => p.in === "query");
		},

		hasRequestBody() {
			if (this.manualMode) {
				return ["POST", "PUT", "PATCH"].includes(this.selectedMethod);
			}
			return this.selectedEndpoint?.requestBody ||
				["POST", "PUT", "PATCH"].includes(this.selectedEndpoint?.method);
		},

		requestBodySchema() {
			if (!this.selectedEndpoint?.requestBody?.content?.["application/json"]?.schema) {
				return null;
			}
			const schema = this.selectedEndpoint.requestBody.content["application/json"].schema;

			return this.resolveSchema(schema);
		},

		requestBodyHints() {
			const schema = this.requestBodySchema;

			if (!schema || !schema.properties) return null;

			const hints = [];
			const required = schema.required || [];

			for (const [fieldName, fieldSchema] of Object.entries(schema.properties)) {
				const isRequired = required.includes(fieldName);
				const type = fieldSchema.type || "any";
				const description = fieldSchema.description || "";
				const example = fieldSchema.example || this.getDefaultValue(fieldSchema);

				hints.push({
					name: fieldName,
					type,
					required: isRequired,
					description,
					example
				});
			}

			return hints;
		},

		fullRequestUrl() {
			let baseUrl = this.axios.defaults.baseURL || "";
			let path = this.manualMode ? this.manualPath : (this.selectedEndpoint?.path || "");

			// Normalize: remove trailing slash from baseUrl, ensure path starts with /
			baseUrl = baseUrl.replace(/\/+$/, "");
			if (path && !path.startsWith("/")) {
				path = "/" + path;
			}

			// Replace path parameters
			for (const [key, value] of Object.entries(this.paramValues)) {
				if (value) {
					path = path.replace(`{${key}}`, encodeURIComponent(value));
				}
			}

			// Add query parameters from endpoint spec
			const queryParts = [];

			for (const param of this.queryParams) {
				const value = this.paramValues[param.name];

				if (value) {
					queryParts.push(`${param.name}=${encodeURIComponent(value)}`);
				}
			}

			// Add custom query parameters
			for (const param of this.customQueryParams) {
				if (param.name && param.value) {
					queryParts.push(`${encodeURIComponent(param.name)}=${encodeURIComponent(param.value)}`);
				}
			}

			if (queryParts.length) {
				path += "?" + queryParts.join("&");
			}

			return baseUrl + path;
		},

		formattedResponse() {
			// Handle empty responses
			if (this.response === null || this.response === undefined || this.response === "") {
				return this.$t("empty-response");
			}
			try {
				return JSON.stringify(this.response, null, 2);
			} catch {
				return String(this.response);
			}
		},

		responseStatusColor() {
			if (!this.responseStatus) return "grey";
			if (this.responseStatus >= 200 && this.responseStatus < 300) return "green";
			if (this.responseStatus >= 400 && this.responseStatus < 500) return "orange";
			if (this.responseStatus >= 500) return "red";
			return "grey";
		},

		responseHeaderClass() {
			if (!this.responseStatus) return "";
			if (this.responseStatus >= 200 && this.responseStatus < 300) return "response-success";
			if (this.responseStatus >= 400) return "response-error-header";
			return "";
		},

		hasResponse() {
			return this.responseStatus !== null || this.responseError !== null;
		}
	},

	watch: {
		requestBody(value) {
			this.validateJson(value);
		}
	},

	created() {
		this.fetchSpec();
	},

	methods: {
		async fetchSpec(options) {
			const silent = options?.silent || false;
			let success = true;

			if (!silent) {
				this.loadingSpec = true;
				this.specError = null;
			}

			try {
				const { data } = await this.axios.get("/docs-json");

				this.spec = data;
				this.parseEndpoints();
			} catch (err) {
				console.error("Failed to fetch OpenAPI spec:", err);
				success = false;
				if (!silent) this.specError = "Failed to load API documentation";
			} finally {
				if (!silent) this.loadingSpec = false;
			}

			return { success };
		},

		parseEndpoints() {
			const endpoints = [];
			const paths = this.spec?.paths || {};

			for (let [path, methods] of Object.entries(paths)) {
				// Strip /api prefix if present (docs already include it but baseURL may also have /api)
				if (path.startsWith("/api/") || path === "/api") {
					path = path.slice(4) || "/";
				}

				for (const [method, details] of Object.entries(methods)) {
					if (["get", "post", "put", "patch", "delete"].includes(method)) {
						endpoints.push({
							id: `${method.toUpperCase()}-${path}`,
							path,
							method: method.toUpperCase(),
							summary: details.summary,
							description: details.description,
							tags: details.tags,
							parameters: details.parameters || [],
							requestBody: details.requestBody,
							responses: details.responses,
							security: details.security,
							isPublic: !details.security || details.security.length === 0
						});
					}
				}
			}

			// Sort by path then method
			endpoints.sort((a, b) => {
				const pathCompare = a.path.localeCompare(b.path);

				if (pathCompare !== 0) return pathCompare;
				return a.method.localeCompare(b.method);
			});

			this.endpoints = endpoints;
		},

		getMethodColor(method) {
			const colors = {
				GET: "blue",
				POST: "green",
				PUT: "orange",
				PATCH: "purple",
				DELETE: "red"
			};

			return colors[method] || "grey";
		},

		selectEndpoint(endpoint) {
			this.selectedEndpoint = endpoint;
			this.paramValues = {};
			this.customQueryParams = [];
			this.requestBody = "{\n\t\n}";
			this.response = null;
			this.responseError = null;
			this.responseStatus = null;
			this.responseTime = null;
			this.responseSize = null;
			this.responseHeaders = null;
			this.showHeaders = false;

			// Initialize params with empty values
			if (endpoint.parameters) {
				for (const param of endpoint.parameters) {
					this.$set(this.paramValues, param.name, "");
				}
			}

			// Initialize request body from schema if available
			if (endpoint.requestBody?.content?.["application/json"]?.schema) {
				const schema = endpoint.requestBody.content["application/json"].schema;

				this.requestBody = this.generateBodyFromSchema(schema);
			}

			// Scroll to request section
			this.$nextTick(() => {
				this.scrollToElement(this.$refs.requestSection);
			});
		},

		generateBodyFromSchema(schema) {
			const emptyBody = "{\n\t\n}";

			if (!schema) return emptyBody;

			// Handle $ref
			if (schema.$ref) {
				const refPath = schema.$ref.replace("#/components/schemas/", "");
				const refSchema = this.spec?.components?.schemas?.[refPath];

				if (refSchema) {
					return this.generateBodyFromSchema(refSchema);
				}
			}

			if (schema.type === "object" || schema.properties) {
				const obj = {};

				for (const [key, prop] of Object.entries(schema.properties || {})) {
					obj[key] = this.getDefaultValue(prop);
				}
				// If no properties, return formatted empty body
				if (Object.keys(obj).length === 0) {
					return emptyBody;
				}
				return JSON.stringify(obj, null, "\t");
			}

			return emptyBody;
		},

		getDefaultValue(prop) {
			if (prop.example !== undefined) return prop.example;
			if (prop.default !== undefined) return prop.default;

			switch (prop.type) {
				case "string": return "";
				case "number":
				case "integer": return 0;
				case "boolean": return false;
				case "array": return [];
				case "object": return {};
				default: return null;
			}
		},

		resolveSchema(schema) {
			if (!schema) return null;

			// Handle $ref
			if (schema.$ref) {
				const refPath = schema.$ref.replace("#/components/schemas/", "");
				const refSchema = this.spec?.components?.schemas?.[refPath];

				if (refSchema) {
					return this.resolveSchema(refSchema);
				}
			}

			return schema;
		},

		async executeRequest() {
			// Validate body JSON
			if (this.hasRequestBody) {
				try {
					JSON.parse(this.requestBody);
					this.bodyError = "";
				} catch {
					this.bodyError = "Invalid JSON";
					return;
				}
			}

			this.executing = true;
			this.response = null;
			this.responseError = null;
			this.responseStatus = null;
			this.responseSize = null;
			this.responseHeaders = null;

			const startTime = Date.now();

			try {
				const method = this.manualMode ? this.selectedMethod.toLowerCase() : this.selectedEndpoint.method.toLowerCase();
				let path = this.manualMode ? this.manualPath : this.selectedEndpoint.path;

				// Replace path parameters
				for (const [key, value] of Object.entries(this.paramValues)) {
					if (value) {
						path = path.replace(`{${key}}`, encodeURIComponent(value));
					}
				}

				// Build query string from endpoint params
				const queryParts = [];

				for (const param of this.queryParams) {
					const value = this.paramValues[param.name];

					if (value) {
						queryParts.push(`${param.name}=${encodeURIComponent(value)}`);
					}
				}

				// Add custom query parameters
				for (const param of this.customQueryParams) {
					if (param.name && param.value) {
						queryParts.push(`${encodeURIComponent(param.name)}=${encodeURIComponent(param.value)}`);
					}
				}

				if (queryParts.length) {
					path += "?" + queryParts.join("&");
				}

				const config = {
					method,
					url: path,
					headers: {
						Authorization: `Bearer ${this.jwt}`
					}
				};

				if (this.hasRequestBody && this.requestBody) {
					config.data = JSON.parse(this.requestBody);
				}

				const res = await this.axios(config);

				this.response = res.data;
				this.responseStatus = res.status;
				this.responseSize = this.calculateResponseSize(res.data);
				this.responseHeaders = this.parseHeaders(res.headers);
			} catch (err) {
				if (err.response) {
					this.response = err.response.data;
					this.responseStatus = err.response.status;
					this.responseSize = this.calculateResponseSize(err.response.data);
					this.responseHeaders = this.parseHeaders(err.response.headers);
				} else {
					this.responseError = err.message || "Request failed";
				}
			} finally {
				this.responseTime = Date.now() - startTime;
				this.executing = false;

				// Scroll to response section
				this.$nextTick(() => {
					this.scrollToElement(this.$refs.responseSection);
				});
			}
		},

		scrollToElement(element) {
			if (!element) return;
			element.scrollIntoView({ behavior: "smooth", block: "start" });
		},

		async copyToken() {
			if (!this.jwt) return;
			try {
				await navigator.clipboard.writeText(this.jwt);
				this.$success(this.$t("copied-to-clipboard"));
			} catch (err) {
				console.error("Failed to copy:", err);
			}
		},

		async copyResponse() {
			if (!this.response) return;
			try {
				await navigator.clipboard.writeText(this.formattedResponse);
				this.$success(this.$t("copied-to-clipboard"));
			} catch (err) {
				console.error("Failed to copy:", err);
			}
		},

		calculateResponseSize(data) {
			if (data === null || data === undefined) return 0;
			try {
				const str = typeof data === "string" ? data : JSON.stringify(data);

				return new Blob([str]).size;
			} catch {
				return 0;
			}
		},

		formatBytes(bytes) {
			if (bytes === 0) return "0 B";
			const units = ["B", "KB", "MB", "GB"];
			const i = Math.floor(Math.log(bytes) / Math.log(1024));
			const size = (bytes / Math.pow(1024, i)).toFixed(i > 0 ? 1 : 0);

			return `${size} ${units[i]}`;
		},

		parseHeaders(headers) {
			if (!headers) return null;
			// Axios returns headers as an object, convert to sorted object
			const result = {};
			const keys = Object.keys(headers).sort();

			for (const key of keys) {
				result[key] = headers[key];
			}
			return Object.keys(result).length > 0 ? result : null;
		},

		validateJson(value) {
			// Handle both direct value and event object from @input
			const text = typeof value === "string" ? value : this.requestBody;

			if (!text || text.trim() === "" || text.trim() === "{\n\t\n}") {
				this.bodyError = "";
				return true;
			}
			try {
				JSON.parse(text);
				this.bodyError = "";
				return true;
			} catch {
				this.bodyError = this.$t("invalid-json");
				return false;
			}
		},

		onPaste() {
			// Validate after paste event is processed
			this.$nextTick(() => {
				this.validateJson(this.requestBody);
			});
		},

		addCustomQueryParam() {
			this.customQueryParams.push({ name: "", value: "" });
		},

		removeCustomQueryParam(index) {
			this.customQueryParams.splice(index, 1);
		},

		breakablePath(path) {
			if (!path) return "";
			// Escape HTML entities then insert <wbr> after each / for natural line-break points
			const escaped = path.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");

			return escaped.replace(/\//g, "/<wbr>");
		}
	}
};
</script>

<style scoped>
	.api-explorer-embedded-card {
		padding: 16px !important;
	}

	.api-explorer-content {
		display: flex;
		flex-direction: column;
	}

	/* Token Section */
	.token-section {
		border-radius: 8px;
		overflow: hidden;
		border: 1px solid #e0e0e0;
	}

	.token-header {
		background: linear-gradient(135deg, #6a1b9a 0%, #4a148c 100%);
		color: white;
		padding: 12px 16px;
		font-weight: 600;
		font-size: 14px;
		display: flex;
		justify-content: space-between;
		align-items: center;
	}

	/* Collapsible Headers */
	.clickable {
		cursor: pointer;
		user-select: none;
	}

	.clickable:hover {
		filter: brightness(1.05);
	}

	.collapse-icon {
		transition: transform 0.2s ease;
	}

	.collapse-icon.collapsed {
		transform: rotate(-90deg);
	}

	.token-preview {
		padding: 12px 16px;
		background: #f5f5f5;
		font-family: monospace;
		font-size: 11px;
	}

	.token-value {
		color: #666;
		word-break: break-all;
	}

	.copy-token-btn {
		text-transform: none;
		opacity: 0.9;
	}

	.copy-token-btn:hover {
		opacity: 1;
		background: rgba(255, 255, 255, 0.1);
	}

	/* Endpoint Section */
	.endpoint-section {
		border-radius: 8px;
		overflow: hidden;
		border: 1px solid #e0e0e0;
	}

	.endpoint-header {
		background: linear-gradient(135deg, #1976d2 0%, #1565c0 100%);
		color: white;
		padding: 12px 16px;
		font-weight: 600;
		font-size: 14px;
		display: flex;
		align-items: center;
	}

	.endpoint-content {
		padding: 16px;
		background: #fafafa;
	}

	.endpoint-list {
		max-height: 340px;
		overflow-y: auto;
		overflow-x: hidden;
		border-radius: 10px;
		background: white;
		border: 1px solid #e8eaed;
		box-shadow: inset 0 1px 3px rgba(0, 0, 0, 0.04);
	}

	.endpoint-item {
		display: flex;
		flex-direction: column;
		padding: 10px 14px;
		border-bottom: 1px solid #f0f1f4;
		cursor: pointer;
		transition: all 0.15s ease;
		position: relative;
	}

	.endpoint-item:last-child {
		border-bottom: none;
	}

	.endpoint-item:hover {
		background: #f4f6ff;
	}

	.endpoint-selected {
		background: #e8ecff !important;
		border-left: 3px solid #3C41C3;
		padding-left: 11px;
	}

	.endpoint-item-main {
		display: flex;
		align-items: center;
		gap: 8px;
		min-width: 0;
	}

	.endpoint-path {
		font-family: 'Fira Code', 'Monaco', 'Consolas', monospace;
		font-size: 12px;
		font-weight: 500;
		color: #37474f;
		line-height: 1.4;
		word-break: break-word;
		overflow-wrap: break-word;
		min-width: 0;
	}

	.endpoint-selected .endpoint-path {
		color: #1a237e;
	}

	.method-chip {
		font-weight: 700;
		font-size: 10px !important;
		min-width: 56px;
		max-width: 56px;
		justify-content: center;
		letter-spacing: 0.3px;
		flex-shrink: 0;
		padding: 0 !important;
		text-align: center;
	}

	.security-icon {
		opacity: 0.65;
		flex-shrink: 0;
	}

	.endpoint-summary {
		font-size: 11px;
		color: #90a4ae;
		line-height: 1.3;
		margin-top: 3px;
		white-space: nowrap;
		overflow: hidden;
		text-overflow: ellipsis;
	}

	.no-results {
		display: flex;
		flex-direction: column;
		align-items: center;
		justify-content: center;
		padding: 40px 24px;
		color: #999;
		gap: 8px;
	}

	/* Endpoint Groups */
	.endpoint-group + .endpoint-group {
		border-top: 1px solid #e8eaed;
	}

	.endpoint-group-header {
		padding: 8px 14px;
		background: #f8f9fb;
		font-size: 10px;
		font-weight: 700;
		color: #78909c;
		text-transform: uppercase;
		letter-spacing: 0.6px;
		display: flex;
		align-items: center;
		gap: 8px;
		position: sticky;
		top: 0;
		z-index: 1;
		border-bottom: 1px solid #eef0f2;
	}

	.group-tag-dot {
		width: 6px;
		height: 6px;
		border-radius: 50%;
		flex-shrink: 0;
	}

	.group-tag-name {
		flex: 1;
	}

	.endpoint-count {
		font-weight: 500;
		color: #b0bec5;
		font-size: 10px;
		background: #eef0f2;
		border-radius: 8px;
		padding: 1px 7px;
		line-height: 1.5;
	}

	/* Request Section */
	.request-section {
		border-radius: 8px;
		overflow: hidden;
		border: 1px solid #e0e0e0;
	}

	.request-header {
		background: linear-gradient(135deg, #00897b 0%, #00695c 100%);
		color: white;
		padding: 12px 16px;
		font-weight: 600;
		font-size: 14px;
		display: flex;
		justify-content: space-between;
		align-items: center;
	}

	.request-content {
		padding: 16px;
		background: #fafafa;
	}

	.request-path {
		display: flex;
		align-items: center;
		background: white;
		padding: 8px 12px;
		border-radius: 4px;
		border: 1px solid #e0e0e0;
	}

	.path-label {
		font-weight: 600;
		font-size: 12px;
		color: #666;
		margin-right: 8px;
	}

	.path-value {
		font-family: monospace;
		font-size: 12px;
		color: #1976d2;
		word-break: break-all;
	}

	.params-section {
		background: white;
		padding: 12px;
		border-radius: 4px;
		border: 1px solid #e0e0e0;
	}

	.params-label {
		font-weight: 600;
		font-size: 12px;
		color: #666;
		margin-bottom: 12px;
		text-transform: uppercase;
		letter-spacing: 0.5px;
	}

	.param-row {
		display: flex;
		align-items: flex-start;
		margin-bottom: 8px;
	}

	.param-row:last-child {
		margin-bottom: 0;
	}

	.param-input {
		flex: 1;
	}

	/* Custom Query Params */
	.custom-param-row {
		align-items: center;
	}

	.param-name-input {
		flex: 0 0 40%;
	}

	.param-value-input {
		flex: 1;
	}

	/* JSON Invalid State */
	.json-invalid >>> .v-input__slot {
		border-color: #f44336 !important;
	}

	.json-invalid >>> fieldset {
		border-color: #f44336 !important;
		border-width: 2px !important;
	}

	.execute-btn {
		text-transform: none;
		font-weight: 600;
	}

	/* Response Section */
	.response-section {
		border-radius: 8px;
		overflow: hidden;
		border: 1px solid #e0e0e0;
	}

	.response-header {
		background: linear-gradient(135deg, #455a64 0%, #37474f 100%);
		color: white;
		padding: 12px 16px;
		font-weight: 600;
		font-size: 14px;
		display: flex;
		justify-content: space-between;
		align-items: center;
	}

	.response-header.response-success {
		background: linear-gradient(135deg, #43a047 0%, #388e3c 100%);
	}

	.response-header.response-error-header {
		background: linear-gradient(135deg, #e53935 0%, #c62828 100%);
	}

	.response-time,
	.response-size {
		font-size: 11px;
		opacity: 0.8;
	}

	.headers-toggle-btn {
		text-transform: none;
		opacity: 0.7;
		transition: all 0.2s ease;
	}

	.headers-toggle-btn:hover,
	.headers-toggle-btn.headers-active {
		opacity: 1;
		background: rgba(255, 255, 255, 0.15);
	}

	.headers-count {
		font-size: 10px;
		opacity: 0.7;
		margin-left: 4px;
	}

	.copy-response-btn {
		text-transform: none;
		opacity: 0.9;
	}

	.copy-response-btn:hover {
		opacity: 1;
		background: rgba(255, 255, 255, 0.1);
	}

	.response-content {
		background: #1e1e1e;
		max-height: 400px;
		overflow: auto;
	}

	.response-headers-panel {
		background: linear-gradient(180deg, #2d2d2d 0%, #252525 100%);
		border-bottom: 1px solid #3d3d3d;
		padding: 12px 16px;
	}

	.headers-panel-header {
		display: flex;
		align-items: center;
		margin-bottom: 10px;
		padding-bottom: 8px;
		border-bottom: 1px solid #3d3d3d;
	}

	.headers-panel-title {
		font-size: 11px;
		font-weight: 600;
		color: #808080;
		text-transform: uppercase;
		letter-spacing: 0.5px;
	}

	.headers-grid {
		display: grid;
		grid-template-columns: auto 1fr;
		gap: 6px 16px;
		font-family: 'Fira Code', 'Monaco', 'Consolas', monospace;
		font-size: 11px;
		line-height: 1.6;
	}

	.header-name {
		color: #9cdcfe;
		font-weight: 500;
		white-space: nowrap;
	}

	.header-name::after {
		content: ':';
		color: #808080;
	}

	.header-value {
		color: #ce9178;
		word-break: break-all;
	}

	.response-body {
		margin: 0;
		padding: 16px;
		font-family: 'Fira Code', 'Monaco', 'Consolas', monospace;
		font-size: 12px;
		line-height: 1.5;
		color: #d4d4d4;
		white-space: pre-wrap;
		word-break: break-word;
	}

	.response-error {
		padding: 16px;
		color: #f44336;
		font-family: monospace;
	}

	/* Manual Input */
	.manual-input {
		background: white;
		padding: 12px;
		border-radius: 4px;
		border: 1px solid #e0e0e0;
	}

	@media (max-width: 599px) {
		.api-explorer-embedded-card {
			padding: 10px !important;
		}

		.token-section,
		.endpoint-section,
		.request-section,
		.response-section {
			border-radius: 6px;
		}

		.endpoint-content {
			padding: 10px;
		}

		.endpoint-list {
			max-height: 360px;
			overflow-x: hidden;
			border-radius: 8px;
		}

		.endpoint-item {
			padding: 10px 12px;
		}

		.endpoint-item-main {
			gap: 6px;
		}

		.endpoint-path {
			font-size: 11px;
			word-break: break-word;
			overflow-wrap: break-word;
		}

		.endpoint-summary {
			display: none;
		}

		.endpoint-group-header {
			padding: 7px 12px;
		}

		.request-content {
			padding: 10px;
		}

		.request-path {
			padding: 6px 8px;
			flex-direction: column;
			align-items: flex-start;
			gap: 4px;
		}

		.path-value {
			font-size: 11px;
		}

		.params-section {
			padding: 8px;
		}

		.response-header {
			flex-wrap: wrap;
			gap: 8px;
		}

		.response-header-actions {
			flex-wrap: wrap;
			gap: 4px;
		}

		.response-body {
			padding: 10px;
			font-size: 11px;
		}

		.manual-input {
			padding: 8px;
		}

	/* Request Body Hints */
	.body-hints-card {
		background: linear-gradient(135deg, #fff9c4 0%, #fffde7 100%) !important;
		border-left: 4px solid #fbc02d !important;
	}

	.body-hints-title {
		color: #f57f17;
		text-transform: uppercase;
		letter-spacing: 0.5px;
	}

	.body-hints-list {
		max-height: 300px;
		overflow-y: auto;
		padding-right: 4px;
	}

	.body-hint-item {
		padding: 6px 8px;
		border-radius: 4px;
		background: rgba(255, 255, 255, 0.6);
		border-left: 2px solid #fbc02d;
		transition: background 0.2s ease;
	}

	.body-hint-item:hover {
		background: rgba(255, 255, 255, 0.9);
	}

	.body-input {
		font-family: monospace;
		font-size: 12px;
	}

	.body-input.json-invalid {
		background-color: rgba(255, 0, 0, 0.05);
	}
	}
</style>
